import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore.js'

function UserManagement() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    namaLengkap: '',
    email: '',
    role: 'teller',
    kodeCabang: '',
    isActive: true
  })

  useEffect(() => {
    fetchUsers()
  }, [page, search, roleFilter])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const params = {
        page,
        limit: 20,
        search,
        role: roleFilter
      }
      const response = await api.get('/users', { params })
      setUsers(response.data.data || [])
      setTotalPages(Math.ceil((response.data.meta?.total || 0) / 20))
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const syncMemberUsers = async () => {
    if (!confirm('Buat user untuk semua anggota yang aktif?\nUsername = NBA, Password default: k2su123')) return
    setSyncing(true)
    try {
      const response = await api.post('/member/sync-users')
      alert(response.data?.message || `Sinkronisasi berhasil! ${response.data?.data?.created || 0} user dibuat.`)
      fetchUsers()
    } catch (error) {
      alert('Gagal: ' + (error.response?.data?.message || error.message))
    } finally {
      setSyncing(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (selectedUser) {
        await api.put(`/users/${selectedUser.id}`, formData)
        alert('User berhasil diupdate')
      } else {
        await api.post('/users', formData)
        alert('User berhasil dibuat')
      }
      setShowModal(false)
      resetForm()
      fetchUsers()
    } catch (error) {
      alert('Gagal menyimpan user: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus user ini?')) {
      return
    }
    try {
      await api.delete(`/users/${id}`)
      alert('User berhasil dihapus')
      fetchUsers()
    } catch (error) {
      alert('Gagal menghapus user: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleEdit = (user) => {
    setSelectedUser(user)
    setFormData({
      username: user.Username,
      password: '',
      namaLengkap: user.NamaLengkap,
      email: user.Email || '',
      role: user.Role,
      kodeCabang: user.KodeCabang || '',
      isActive: user.IsActive
    })
    setShowModal(true)
  }

  const handleChangePassword = (user) => {
    setSelectedUser(user)
    setFormData({ password: '' })
    setShowPasswordModal(true)
  }

  const handleSubmitPassword = async (e) => {
    e.preventDefault()
    try {
      await api.post(`/users/${selectedUser.Id}/change-password`, { password: formData.password })
      alert('Password berhasil diubah')
      setShowPasswordModal(false)
      setFormData({ password: '' })
    } catch (error) {
      alert('Gagal mengubah password: ' + (error.response?.data?.message || error.message))
    }
  }

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      namaLengkap: '',
      email: '',
      role: 'teller',
      kodeCabang: '',
      isActive: true
    })
    setSelectedUser(null)
  }

  const openAddModal = () => {
    resetForm()
    setShowModal(true)
  }

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'danger',
      manager: 'warning',
      teller: 'info',
      accounting: 'secondary'
    }
    return colors[role] || 'secondary'
  }

  if (loading && users.length === 0) {
    return (
      <div className="container-fluid p-3">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid p-3">
      <div className="card shadow">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0 font-weight-bold">Manajemen User</h5>
          <div>
            <button className="btn btn-sm btn-info mr-2" onClick={syncMemberUsers} disabled={syncing}>
              <i className="fas fa-sync mr-1"></i>{syncing ? 'Memproses...' : 'Sinkron Anggota'}
            </button>
            <button className="btn btn-sm btn-light" onClick={openAddModal}>
              <i className="fas fa-plus mr-1"></i>Tambah User
            </button>
          </div>
        </div>
        <div className="card-header py-2 bg-light d-flex align-items-center">
          <div className="input-group input-group-sm" style={{ maxWidth: 280 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Cari user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="input-group-append">
              <span className="input-group-text"><i className="fas fa-search"></i></span>
            </div>
          </div>
          <select
            className="form-control form-control-sm ml-2"
            style={{ maxWidth: 160 }}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">Semua Role</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="teller">Teller</option>
            <option value="accounting">Accounting</option>
            <option value="member">Member</option>
          </select>
        </div>
        <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Nama Lengkap</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Cabang</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.Id}>
                        <td>{user.Username}</td>
                        <td>{user.NamaLengkap}</td>
                        <td>{user.Email || '-'}</td>
                        <td>
                          <span className={`badge badge-${getRoleBadge(user.Role)}`}>
                            {user.Role}
                          </span>
                        </td>
                        <td>{user.KodeCabang || '-'}</td>
                        <td>
                          <span className={`badge badge-${user.IsActive ? 'success' : 'danger'}`}>
                            {user.IsActive ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-info mr-1"
                            onClick={() => handleEdit(user)}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-warning mr-1"
                            onClick={() => handleChangePassword(user)}
                          >
                            <i className="fas fa-key"></i>
                          </button>
                          {parseInt(user.Id) !== parseInt(user?.Id) && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(user.Id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          Tidak ada data user
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer">
              <nav>
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      &laquo;
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedUser ? 'Edit User' : 'Tambah User'}</h5>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      className="form-control"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      disabled={!!selectedUser}
                    />
                  </div>
                  {!selectedUser && (
                    <div className="form-group">
                      <label>Password</label>
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Nama Lengkap</label>
                    <input
                      type="text"
                      className="form-control"
                      name="namaLengkap"
                      value={formData.namaLengkap}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      className="form-control"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="teller">Teller</option>
                      <option value="accounting">Accounting</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Kode Cabang (Opsional)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="kodeCabang"
                      value={formData.kodeCabang}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="isActive">
                        Aktif
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Batal
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {selectedUser ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ubah Password</h5>
                <button type="button" className="close" onClick={() => setShowPasswordModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmitPassword}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Password Baru</label>
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength="6"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                    Batal
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Ubah Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
